/**
 * SessionTableSection — Collapsible session table section for ManagerScheduleView.
 * Renders a colored banner row + session data rows with 11 columns.
 * Extracted from ManagerScheduleView.tsx to deduplicate 4 identical session sections.
 */
import React from 'react';
import { ChevronDown, Pencil, Trash2 } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { getStatusBadgeClass } from '../../../utils/statusColors';
import { isSessionCancellable } from '../../../utils/sessionStatus';

interface SectionStyle {
  bannerBg: string;        // e.g. 'bg-green-50 dark:bg-green-900/20'
  bannerBorder: string;    // e.g. 'border-green-200 dark:border-green-800'
  bannerHover: string;     // e.g. 'hover:bg-green-100 dark:hover:bg-green-900/30'
  chevronColor: string;    // e.g. 'text-green-600 dark:text-green-400'
  dotColor: string;        // e.g. 'bg-green-500'
  dotExtra?: string;       // e.g. 'animate-pulse'
  labelColor: string;      // e.g. 'text-green-700 dark:text-green-300'
}

type ActionMode = 'cancel-only' | 'edit-cancel' | 'none';

interface SessionTableSectionProps {
  sessions: any[];
  label: string;
  style: SectionStyle;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  rowOpacity?: string;        // e.g. 'opacity-75', 'opacity-60'
  canManageSessions: boolean;
  actionMode: ActionMode;
  onSessionClick: (session: any) => void;
  onEditClick?: (session: any) => void;
  onCancelClick?: (sessionId: string, status: string) => void;
}

const SessionTableSection: React.FC<SessionTableSectionProps> = ({
  sessions,
  label,
  style,
  isExpanded,
  onToggleExpanded,
  rowOpacity,
  canManageSessions,
  actionMode,
  onSessionClick,
  onEditClick,
  onCancelClick,
}) => {
  const { t } = useLanguage();

  if (sessions.length === 0) return null;

  return (
    <>
      {/* Banner row */}
      <tr
        className={`cursor-pointer ${style.bannerHover} transition-colors`}
        onClick={onToggleExpanded}
      >
        <td
          colSpan={canManageSessions ? 12 : 11}
          className={`px-4 py-3 ${style.bannerBg} border-b ${style.bannerBorder}`}
        >
          <div className="flex items-center gap-2">
            <ChevronDown
              className={`w-4 h-4 ${style.chevronColor} transition-transform ${
                isExpanded ? 'rotate-0' : '-rotate-90'
              }`}
            />
            <div className={`w-2 h-2 rounded-full ${style.dotColor} ${style.dotExtra || ''}`} />
            <span className={`text-sm font-semibold ${style.labelColor}`}>
              {label} ({sessions.length})
            </span>
          </div>
        </td>
      </tr>

      {/* Session rows */}
      {isExpanded && sessions.map((session: any) => (
        <tr
          key={session.session_number}
          onClick={() => onSessionClick(session)}
          className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${rowOpacity || ''}`}
        >
          <td className="px-3 py-3 whitespace-nowrap">
            <span className="font-mono text-sm text-indigo-600 dark:text-indigo-400">
              {session.session_number || '-'}
            </span>
          </td>
          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
            {session.scheduled_date
              ? new Date(session.scheduled_date.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
              : '-'}
          </td>
          <td className="px-3 py-3 whitespace-nowrap">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {session.product_sku}
            </span>
          </td>
          <td className="px-3 py-3 max-w-[150px]">
            <div className="text-sm text-gray-900 dark:text-white">
              {session.product_name}
            </div>
            {session.product_chinese_name && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {session.product_chinese_name}
              </div>
            )}
          </td>
          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
            {session.packing || '-'}
          </td>
          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
            {session.total_boxes || '-'}
          </td>
          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
            {session.target_output_weight
              ? `${Number(session.target_output_weight).toFixed(2)} lbs`
              : '-'}
          </td>
          <td className="px-3 py-3 max-w-[140px]">
            <div className="text-sm text-gray-900 dark:text-white">
              {session.raw_material_item_number || '-'}
            </div>
            {session.raw_material_name && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {session.raw_material_name}
              </div>
            )}
          </td>
          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[100px]">
            {/* WCO-2351: show "Multiple" when the session's raw materials span >1 brand
                (distinct_brand_count arrives as a string from the pg bigint COUNT) */}
            {Number(session.distinct_brand_count) > 1 ? t('supervisor.multipleBrands') : (session.brand || '-')}
          </td>
          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
            {session.material_location_code || '-'}
          </td>
          <td className="px-3 py-3">
            <span
              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatusBadgeClass(
                session.status
              )}`}
            >
              {t(`status.${session.status}`) || session.status}
            </span>
          </td>
          {canManageSessions && (
            <td className="px-3 py-3 text-center">
              {/* WCO-2706: running/finished sessions can no longer be cancelled */}
              {actionMode === 'cancel-only' && isSessionCancellable(session.status) && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); onCancelClick?.(session.session_number, session.status); }}
                    className="p-1.5 text-orange-600 hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-900/30 rounded-md transition-colors"
                    title={t('supervisor.cancelSession')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              {actionMode === 'edit-cancel' && (
                <div className="flex items-center justify-center gap-2">
                  {/* WCO-2526: pending = full edit; submitted/received = planned-date edit only. */}
                  {['pending', 'submitted', 'received'].includes(session.status) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onEditClick?.(session); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                      title={t('common.edit')}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {isSessionCancellable(session.status) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onCancelClick?.(session.session_number, session.status); }}
                      className="p-1.5 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30 rounded-md transition-colors"
                      title={t('supervisor.cancelSession')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
              {actionMode === 'none' && null}
            </td>
          )}
        </tr>
      ))}
    </>
  );
};

export default SessionTableSection;
